import {type ComponentType, forwardRef, type SVGProps} from 'react';

export type SvgIconSize = 'sm' | 'md' | 'lg' | number;

const SIZE_MAP: Record<Exclude<SvgIconSize, number>, number> = {
  sm: 16,
  md: 20,
  lg: 24,
};

export interface SvgIconProps extends Omit<SVGProps<SVGSVGElement>, 'ref'> {
  as: ComponentType<SVGProps<SVGSVGElement>>;
  size?: SvgIconSize;
  label?: string;
}

const SvgIcon = forwardRef<SVGSVGElement, SvgIconProps>(
  ({as: Component, size = 'md', label, ...props}, ref) => {
    const px = typeof size === 'number' ? size : SIZE_MAP[size];

    const labeledFromProp = label !== undefined;
    const labeledFromAria = 'aria-label' in props || 'aria-labelledby' in props;
    const isLabeled = labeledFromProp || labeledFromAria;

    const a11y: SVGProps<SVGSVGElement> = isLabeled
      ? {
          role: 'img',
          ...(labeledFromProp && {'aria-label': label}),
        }
      : {'aria-hidden': true};

    return (
      <Component
        ref={ref}
        width={px}
        height={px}
        focusable={false}
        {...a11y}
        {...props}
      />
    );
  },
);

SvgIcon.displayName = 'SvgIcon';

export {SvgIcon};
